/**
 * The frame and the surfaces: the sidebar, the page header, cards, the stat
 * strip, feature tiles, the chooser, tables and the empty state. Styles in
 * styles/base.css and styles/components.css.
 */

import type { ComponentChildren } from "preact";
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { WORDMARK } from "../brand";
import { Icon, type IconName } from "./icons";

/* ------------------------------------------------------------------ */
/* The sidebar                                                         */
/* ------------------------------------------------------------------ */

export interface SideLink {
  readonly id: string;
  readonly label: string;
  readonly href: string;
}

/** A row of the first level, with an icon, or a section whose pages sit under it. */
export type SideItem =
  | (SideLink & { readonly kind: "top"; readonly icon: IconName })
  | { readonly kind: "section"; readonly label: string; readonly icon: IconName; readonly items: readonly SideLink[] };

/** One of the student's recent documents or searches, as Harvey lists its threads. */
export interface RecentLink {
  readonly id: string;
  readonly label: string;
  /** The feature and when: "Plagiarism · 14 Oct". */
  readonly detail: string;
  readonly href: string;
}

export interface SidebarMe {
  readonly initials: string;
  readonly name: string;
  readonly detail: string;
  readonly settingsHref?: string;
  /** What the account opens: the theme and Log out. With it the account row is a menu button. */
  readonly menu?: ComponentChildren;
}

/**
 * Harvey's sidebar: dark in both themes, two levels (a section and the pages
 * under it, never a third), the student's recent documents and searches under
 * them, rows at the foot, and the student with their plan at the bottom.
 * `current` is the id of the page showing, marked aria-current.
 *
 * `hidden` is for a drawer off screen or a sidebar folded away: the whole of it
 * leaves the tab order and the accessibility tree, so a keyboard cannot walk
 * into links nobody can see.
 */
export function Sidebar(props: {
  readonly brand: ComponentChildren;
  readonly items: readonly SideItem[];
  readonly foot?: readonly (SideLink & { readonly icon: IconName })[];
  readonly current: string | null;
  readonly me?: SidebarMe;
  readonly recents?: { readonly items: readonly RecentLink[]; readonly empty: string };
  readonly id?: string;
  readonly hidden?: boolean;
  readonly onClose?: () => void;
  /** A link in it was followed, including one to the page already showing. */
  readonly onFollow?: () => void;
}) {
  const at = (id: string) => (props.current === id ? ("page" as const) : undefined);
  const followed = (event: MouseEvent) => {
    if (props.onFollow && event.target instanceof Element && event.target.closest("a")) props.onFollow();
  };
  return (
    <aside class="sidebar" id={props.id} aria-label="Main" inert={props.hidden} onClick={followed}>
      <div class="sidebar-brand">
        {props.brand}
        {props.onClose ? (
          <button type="button" class="icon-btn" aria-label="Close menu" aria-controls={props.id} onClick={props.onClose}>
            <Icon name="panel" />
          </button>
        ) : null}
      </div>
      <div class="sidebar-scroll">
        <nav class="sidebar-nav" aria-label="Sections">
          {props.items.map((item) =>
            item.kind === "top" ? (
              <a key={item.id} class="sidebar-top" href={item.href} aria-current={at(item.id)}>
                <Icon name={item.icon} />
                {item.label}
              </a>
            ) : (
              <div key={item.label} class="sidebar-group" role="group" aria-label={item.label}>
                <div class="sidebar-sec">
                  <Icon name={item.icon} />
                  {item.label}
                </div>
                {item.items.map((sub) => (
                  <a key={sub.id} class="sidebar-sub" href={sub.href} aria-current={at(sub.id)}>
                    {sub.label}
                  </a>
                ))}
              </div>
            ),
          )}
        </nav>
        {props.recents ? (
          <section class="sidebar-recents" aria-label="Recents">
            <h2 class="sidebar-label">Recents</h2>
            {props.recents.items.length > 0 ? (
              <ul>
                {props.recents.items.map((recent) => (
                  <li key={recent.id}>
                    <a class="sidebar-recent" href={recent.href}>
                      <span>{recent.label}</span>
                      <small>{recent.detail}</small>
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <p class="sidebar-none">{props.recents.empty}</p>
            )}
          </section>
        ) : null}
      </div>
      {props.foot && props.foot.length > 0 ? (
        <div class="sidebar-foot">
          {props.foot.map((row) => (
            <a key={row.id} class="sidebar-top" href={row.href} aria-current={at(row.id)}>
              <Icon name={row.icon} />
              {row.label}
            </a>
          ))}
        </div>
      ) : null}
      {props.me ? props.me.menu ? <AccountMenu me={props.me} current={props.current} hidden={props.hidden ?? false} /> : <Me me={props.me} /> : null}
    </aside>
  );
}

function Who(props: { readonly me: SidebarMe }) {
  return (
    <>
      <span class="avatar" aria-hidden="true">
        {props.me.initials}
      </span>
      <span class="sidebar-who">
        <b>{props.me.name}</b>
        <span>{props.me.detail}</span>
      </span>
    </>
  );
}

function Me(props: { readonly me: SidebarMe }) {
  return (
    <div class="sidebar-me">
      <Who me={props.me} />
      {props.me.settingsHref ? (
        <a class="icon-btn" href={props.me.settingsHref} aria-label="Settings" title="Settings">
          <Icon name="gear" />
        </a>
      ) : null}
    </div>
  );
}

/**
 * The student at the foot of the sidebar, as a menu button: it opens upward
 * over the sidebar, with the theme and Log out. A native disclosure, so it
 * opens from the keyboard with nothing written by hand; Escape and a click
 * outside close it, and so does any navigation and the drawer closing.
 */
function AccountMenu(props: { readonly me: SidebarMe; readonly current: string | null; readonly hidden: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDetailsElement>(null);

  useLayoutEffect(() => setOpen(false), [props.current]);
  useLayoutEffect(() => {
    if (props.hidden) setOpen(false);
  }, [props.hidden]);

  useEffect(() => {
    if (!open) return;
    const outside = (event: Event) => {
      if (ref.current && event.target instanceof Node && !ref.current.contains(event.target)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      ref.current?.querySelector("summary")?.focus();
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  return (
    <details class="sidebar-account" ref={ref} open={open} onToggle={(event) => setOpen((event.currentTarget as HTMLDetailsElement).open)}>
      <summary class="sidebar-me" aria-label={`Account menu for ${props.me.name}`}>
        <Who me={props.me} />
        <Icon name="updown" />
      </summary>
      <div class="sidebar-menu">{props.me.menu}</div>
    </details>
  );
}

/** Hallquill's wordmark, as the prototype sets it: the serif, "quill" in the grey. */
export function Wordmark() {
  return (
    <span class="wordmark">
      {WORDMARK.lead}
      <i>{WORDMARK.tail}</i>
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* The page                                                            */
/* ------------------------------------------------------------------ */

export interface TabLink {
  readonly id: string;
  readonly label: string;
  readonly href: string;
}

/**
 * The parts of one page, each at an address of its own (Settings' Profile,
 * Plan, Sign in, Your data): words in a row over a hairline, the current one
 * in the ink and underlined. A phone scrolls the row in its own box.
 */
export function Tabs(props: { readonly label: string; readonly tabs: readonly TabLink[]; readonly current: string | null }) {
  return (
    <nav class="tabs" aria-label={props.label}>
      {props.tabs.map((tab) => (
        <a key={tab.id} href={tab.href} aria-current={props.current === tab.id ? "page" : undefined}>
          {tab.label}
        </a>
      ))}
    </nav>
  );
}

/**
 * Every page opens so: a small line over it and its name in the serif, then
 * the page itself. No line of description under the title (Mohit, 22 October
 * 2026): the title says what the page is.
 */
export function PageHeader(props: {
  readonly eyebrow?: string;
  readonly title: string;
  readonly actions?: ComponentChildren;
}) {
  return (
    <header class="page-head">
      <div>
        {props.eyebrow ? <p class="eyebrow">{props.eyebrow}</p> : null}
        <h1>{props.title}</h1>
      </div>
      {props.actions ? <div class="page-head-actions">{props.actions}</div> : null}
    </header>
  );
}

/** A card. With a title it has a header (and a link at its right); `bare` leaves the room to its parts. */
export function Card(props: {
  readonly title?: string;
  readonly more?: { readonly label: string; readonly href: string };
  readonly bare?: boolean;
  readonly label?: string;
  readonly children: ComponentChildren;
}) {
  if (props.title) {
    return (
      <section class="card bare" aria-label={props.label}>
        <div class="card-h">
          <h2>{props.title}</h2>
          {props.more ? (
            <a class="more" href={props.more.href}>
              {props.more.label}
            </a>
          ) : null}
        </div>
        <div class="card-b">{props.children}</div>
      </section>
    );
  }
  return (
    <section class={props.bare ? "card bare" : "card"} aria-label={props.label}>
      {props.children}
    </section>
  );
}

export interface StatItem {
  readonly label: string;
  readonly value: string;
  readonly note?: string;
}

/** Figures at a glance, in one card: the number large in the serif. */
export function StatStrip(props: { readonly label: string; readonly stats: readonly StatItem[] }) {
  return (
    <section class="card bare stats" aria-label={props.label}>
      {props.stats.map((stat) => (
        <Stat key={stat.label} {...stat} />
      ))}
    </section>
  );
}

export function Stat(props: StatItem) {
  return (
    <div class="stat">
      <span>{props.label}</span>
      <b class="num">{props.value}</b>
      {props.note ? <span>{props.note}</span> : null}
    </div>
  );
}

/** A way into a feature: its name and one line, over a hairline. No icon tile. */
export function FeatureTile(props: { readonly name: string; readonly line: string; readonly href: string }) {
  return (
    <a class="feat" href={props.href}>
      <b>{props.name}</b>
      <span>{props.line}</span>
    </a>
  );
}

/** Ways into features under their section's name, as an index. */
export function FeatureGroup(props: { readonly label: string; readonly children: ComponentChildren }) {
  return (
    <div class="fgroup" role="group" aria-label={props.label}>
      <span class="label">{props.label}</span>
      <div class="feats">{props.children}</div>
    </div>
  );
}

export interface ChoiceOption {
  readonly id: string;
  readonly name: string;
  readonly line: string;
  /** What it costs and takes: "12 Credits · back in 3 working days". */
  readonly meta?: string;
}

/** Two or three choices, one picked before anything else happens. Words only, no icon tile. */
export function Chooser(props: { readonly label: string; readonly options: readonly ChoiceOption[]; readonly onPick: (id: string) => void }) {
  return (
    <div class={props.options.length === 3 ? "chooser three" : "chooser"} role="group" aria-label={props.label}>
      {props.options.map((option) => (
        <button key={option.id} type="button" class="pick" onClick={() => props.onPick(option.id)}>
          <b>{option.name}</b>
          <span>{option.line}</span>
          {option.meta ? <small>{option.meta}</small> : null}
        </button>
      ))}
    </div>
  );
}

export interface Column {
  readonly key: string;
  readonly label: string;
  /** Figures: tabular numbers, set on the right. */
  readonly numeric?: boolean;
}

/** A table that scrolls inside its own box on a phone, never the page. */
export function Table(props: {
  readonly caption: string;
  readonly columns: readonly Column[];
  readonly rows: readonly Readonly<Record<string, ComponentChildren>>[];
}) {
  return (
    <div class="table-wrap">
      <table class="table">
        <caption>{props.caption}</caption>
        <thead>
          <tr>
            {props.columns.map((column) => (
              <th key={column.key} scope="col" class={column.numeric ? "num" : undefined}>
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {props.rows.map((row, at) => (
            <tr key={at}>
              {props.columns.map((column) => (
                <td key={column.key} class={column.numeric ? "num" : undefined}>
                  {row[column.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Nothing here yet: one line in the serif, what the place is for, and the way in. */
export function EmptyState(props: { readonly title: string; readonly line: string; readonly action?: ComponentChildren }) {
  return (
    <div class="empty">
      <h2>{props.title}</h2>
      <p>{props.line}</p>
      {props.action ?? null}
    </div>
  );
}
